import { useCallback, useEffect, useMemo, useState } from "react";
import GreenhouseRunForm from "components/experiment/GreenhouseRunForm";
import CalibrationPlanEditor from "components/experiment/designer/CalibrationPlanEditor";
import PlanPreviewChart from "components/experiment/designer/PlanPreviewChart";
import PresetGallery from "components/experiment/designer/PresetGallery";
import { getExperimentPresets } from "services/experimentApi";
import { stressPlanSeries } from "utils/planPreview";

const TAB_KEY = "padihat_experiment_designer_tab";
const readTab = () => {
  try {
    return localStorage.getItem(TAB_KEY) || "all";
  } catch {
    return "all";
  }
};

function Step({ number, title, children }) {
  return (
    <section className="flex flex-col gap-3">
      <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-700 text-xs text-white">{number}</span>
        {title}
      </h3>
      {children}
    </section>
  );
}

/**
 * The Experiment tab's run designer: 1 pick a preset (built-in, the team's saved ones, or blank), 2 adjust it with a
 * live picture of the plan, 3 start. A stress run is started by GreenhouseRunForm (the same start-run as always); a
 * calibration campaign by CalibrationPlanEditor. While a stress run is active the form shows it (Stop); while a
 * campaign runs the page shows its progress instead of the designer.
 */
export default function ExperimentDesigner({
  controllerMac,
  waterDeviceMac,
  activeController,
  profiles,
  onRefreshProfiles,
  onCreateProfile,
  onArchiveProfile,
  pumpConfig,
  onStartStress,
  onStopStress,
  disabled,
  onCampaignStarted,
}) {
  const [builtins, setBuiltins] = useState([]);
  const [rules, setRules] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [tab, setTabState] = useState(readTab);
  const [selected, setSelected] = useState(null);
  const [stressPlan, setStressPlan] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getExperimentPresets()
      .then((result) => {
        if (cancelled) return;
        setBuiltins(result.presets || []);
        setRules(result.rules || null);
      })
      .catch((error) => !cancelled && setLoadError(error.message));
    return () => {
      cancelled = true;
    };
  }, []);

  const setTab = (value) => {
    setTabState(value);
    try {
      localStorage.setItem(TAB_KEY, value);
    } catch {
      // A per-browser convenience only.
    }
  };

  const select = useCallback((preset) => setSelected({ ...preset, nonce: Date.now() }), []);
  const stressProfiles = useMemo(() => (profiles || []).filter((p) => (p.kind || "stress") === "stress"), [profiles]);
  const savedPresets = useMemo(() => profiles || [], [profiles]);
  const activeRun = activeController?.active_run;

  const stressPoints = useMemo(
    () => (stressPlan ? stressPlanSeries(stressPlan.steps, stressPlan.waterEvents, { startLevelCm: stressPlan.startLevelCm }) : []),
    [stressPlan]
  );

  const archive = async (id) => {
    await onArchiveProfile(id);
    setSelected(null);
  };

  // An active stress run: the form shows it with Stop, nothing to design.
  if (activeRun) {
    return (
      <GreenhouseRunForm
        activeController={activeController}
        disabled={disabled}
        onStart={onStartStress}
        onStop={onStopStress}
        profiles={stressProfiles}
        onCreateProfile={onCreateProfile}
        onArchiveProfile={onArchiveProfile}
        pumpConfig={pumpConfig}
        waterDeviceMac={waterDeviceMac}
      />
    );
  }

  return (
    <div className="flex flex-col gap-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="experiment-designer">
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-blue-700">New experiment</p>
        <p className="text-sm text-slate-500">
          A <strong>stress run</strong> follows a timed temperature and humidity profile. A <strong>calibration</strong> tests the
          sensor at several temperatures and humidities, moving the water up and down at each one.
        </p>
      </div>
      {loadError && <p role="alert" className="text-sm text-red-700">Presets could not be loaded: {loadError}</p>}
      <Step number={1} title="Choose a starting point">
        <PresetGallery
          builtins={builtins}
          saved={savedPresets}
          selectedKey={selected?.key}
          onSelect={select}
          tab={tab}
          onTabChange={setTab}
          defaultLadder={rules?.ladder}
        />
      </Step>
      {selected && selected.kind === "stress" && (
        <Step number={2} title={selected.blank ? "Build the stress run" : `Adjust "${selected.name}" and start`}>
          <PlanPreviewChart points={stressPoints} />
          {stressPlan?.waterEvents?.length > 0 && (stressPlan.startLevelCm === "" || stressPlan.startLevelCm == null) && (
            <p className="text-xs text-slate-500" data-testid="preview-level-hint">
              Enter the starting water level (read it off the ruler, under the water events) to see the water in the preview.
            </p>
          )}
          <GreenhouseRunForm
            activeController={activeController}
            disabled={disabled}
            onStart={onStartStress}
            onStop={onStopStress}
            profiles={stressProfiles}
            onCreateProfile={onCreateProfile}
            onArchiveProfile={onArchiveProfile}
            pumpConfig={pumpConfig}
            waterDeviceMac={waterDeviceMac}
            preset={selected}
            onPlanChange={setStressPlan}
          />
        </Step>
      )}
      {selected && selected.kind === "calibration" && (
        <Step number={2} title={selected.blank ? "Build the calibration" : `Adjust "${selected.name}" and start`}>
          <CalibrationPlanEditor
            preset={selected}
            rules={rules}
            controllerMac={controllerMac}
            waterDeviceMac={waterDeviceMac}
            onSaved={onRefreshProfiles}
            onArchive={archive}
            onStarted={onCampaignStarted}
          />
        </Step>
      )}
      {!selected && <p className="text-sm text-slate-400">Pick a card above to see and adjust its plan.</p>}
    </div>
  );
}
